// src/Pages/Auth/Login.tsx
import React, { useState, useEffect } from 'react';
import { request } from '../../Utils/request';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import Alert from '@mui/material/Alert';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import CryptoJS from 'crypto-js';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { login } from '../../store/slices/AuthSlice';
import { setUser } from '../../store/slices/User';
import GoogleSignInButton from '../../Components/GoogleSignInButton';
import AppleSignInButton from '../../Components/AppleSignInButton';
import FacebookSignInButton from '../../Components/FacebookSignInButton';
import { handleUserEntryInFirestore } from '../../services/CommonApiCall';
import { generateAndRegisterFcmToken } from '../../Utils/fcmTokenHelper';

const Login = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [form, setForm] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const hashPassword = (password: string, uniqueKey: string): string => {
    const hash = CryptoJS.HmacSHA256(password, uniqueKey);
    return hash.toString();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const hashedPassword = hashPassword(form.password, 'ifinds');
      const formWithHashedPassword = { ...form, password: hashedPassword };
      console.log('formWithHashedPassword login', formWithHashedPassword);
      const res = await request<any, typeof form>('/auth/login', {
        method: 'POST',
        body: formWithHashedPassword,
      });
      if (res.token) {
        dispatch(login(res.token));
        // dispatch(setUser(res.user));
        console.log('login res', res);
        // Handle FCM token generation
        const fcmToken = await generateAndRegisterFcmToken();
        navigate('/');
      
        try {
          const ProfileResponse = await request<any, typeof form>(`/profile/${res.user.id}`, {
              method: 'GET',
          });
          console.log(ProfileResponse);
          dispatch(setUser(ProfileResponse.data));
          // const userdataObj = {username:res.user.username}
          const userdataObj = {
            username: res.user?.username,
            email: res.user?.email,
            firstName: res.user?.firstName,
            lastName: res.user?.lastName,
          };
          handleUserEntryInFirestore(res.user.id, userdataObj, fcmToken || '');
        } catch (error) {
          console.log(error);
        }
      }
    //   navigate('/dashboard');
    } catch (error: any) {
      setError(error.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignInError = (error: string) => {
    setError(error);
  };

  // Handle browser back button to go to dashboard
  useEffect(() => {
    const handlePopState = () => {
      // When user presses browser back button, navigate to dashboard
      navigate('/dashboard');
    };

    // Add event listener for browser back button
    window.addEventListener('popstate', handlePopState);

    // Cleanup
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [navigate]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#ffffff' }}>
      <Paper 
       style={{ padding: 32, minWidth: 350, maxWidth: 400 }}
      >
        {/* Heading */}
        <Typography variant="h4" align="center" gutterBottom sx={{ fontWeight: 600 }}>
          Welcome Back
        </Typography>
        <Typography variant="body2" align="center" gutterBottom sx={{ color: 'text.secondary' }}>
          Sign in to continue your journey
        </Typography>

        {/* Email/Password Login Section */}
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <TextField name="email" label="Email" type="email" value={form.email} onChange={handleChange} required />
          <TextField 
            name="password" 
            label="Password" 
            type={showPassword ? 'text' : 'password'} 
            value={form.password} 
            onChange={handleChange} 
            required
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    onClick={togglePasswordVisibility}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {/* Forgot password link */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Link component="button" variant="body2" onClick={() => navigate('/forgot-password')} sx={{ color: '#00796B', textDecoration: 'none' }}>
              Forgot password?
            </Link>
          </Box>

          <Button type="submit" variant="contained" style={{ marginTop: 8, backgroundColor: '#FF6B35', textTransform: 'none', borderRadius: 25, fontWeight: 600 }} fullWidth>
            {loading ? 'Logging in...' : 'Submit'}
          </Button>

        {/* Divider */}
        <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, mb: 2 }}>
          <Divider sx={{ flexGrow: 1 }} />
          <Typography variant="body2" sx={{ mx: 2, color: 'text.secondary' }}>
            or
          </Typography>
          <Divider sx={{ flexGrow: 1 }} />
        </Box>

        {/* Social sign-in buttons */}
        <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <GoogleSignInButton
            onError={handleGoogleSignInError}
            onSuccess={async (idToken: string) => {
              try {
                const res = await request<any, { idToken: string }>(
                  '/auth/firebase/google',
                  {
                    method: 'POST',
                    body: { idToken },
                  }
                );
                
                // Expecting backend to return a token and user profile
                let fcmToken = null;
                if (res.token) {
                  dispatch(login(res.token));
                  fcmToken = await generateAndRegisterFcmToken();
                }
                console.log("backend res google login", res);
                
                const userdataObj = {
                  username:res.user.username,
                  email:res.user.email,
                  firstName:res.user.firstName,
                  lastName:res.user.lastName,
                  fcmToken: fcmToken || null, // Add FCM token for Google sign-in
                };
                // handleUserEntryInFirestore(res.user.id, res.user.username);
                handleUserEntryInFirestore(res.user.id, userdataObj, fcmToken || '');

                // If user data is returned directly, use it; otherwise fetch profile
                if (res.user) {
                  console.log("user1", res.user);
                  dispatch(setUser(res.user));
                } else if (res.userId || res.user_id || res.id) {
                  console.log("user2", );
                  // Fallback: fetch the profile using returned identifier keys
                  const userId = res.userId || res.user_id || res.id;
                  try {
                    const profileRes = await request<any, null>(`/profile/${userId}`, {
                      method: 'GET',
                    });
                    dispatch(setUser(profileRes.data));
                  } catch (profileErr) {
                    console.error('Failed to fetch user profile:', profileErr);
                  }
                }

                navigate('/');
              } catch (err: any) {
                console.error('Google sign-in backend sync failed:', err);
                setError(err.message || 'Google sign-in failed');
              }
            }}
            variant="outlined"
          />
          <AppleSignInButton
            onError={handleGoogleSignInError}
            onSuccess={() => navigate('/')}
            variant="outlined"
          />
          <FacebookSignInButton
            onError={handleGoogleSignInError}
            onSuccess={async (idToken: string) => {
              console.log(" idToken facebook", idToken);
              try {
                const res = await request<any, { idToken: string }>(
                  '/auth/firebase/facebook',
                  {
                    method: 'POST',
                    body: { idToken },
                  }
                );
                console.log(" idToken facebook1", res);
                
                // Expecting backend to return a token and user profile
                let fcmToken = null;
                if (res.token) {
                  dispatch(login(res.token));
                  fcmToken = await generateAndRegisterFcmToken();
                }
                console.log("backend res facebook login", res);

                const userdataObj = {
                  username: res.user?.username,
                  email: res.user?.email,
                  firstName: res.user?.firstName,
                  lastName: res.user?.lastName,
                  fcmToken: fcmToken || null, // Add FCM token for Facebook sign-in
                };
                handleUserEntryInFirestore(res.user?.id, userdataObj, fcmToken ?? '');

                // If user data is returned directly, use it; otherwise fetch profile
                if (res.user) {
                  console.log("user1", res.user);
                  dispatch(setUser(res.user));
                } else if (res.userId || res.user_id || res.id) {
                  console.log("user2", );
                  // Fallback: fetch the profile using returned identifier keys
                  const userId = res.userId || res.user_id || res.id;
                  try {
                    const profileRes = await request<any, null>(`/profile/${userId}`, {
                      method: 'GET',
                    });
                    dispatch(setUser(profileRes.data));
                  } catch (profileErr) {
                    console.error('Failed to fetch user profile:', profileErr);
                  }
                }

                navigate('/');
              } catch (err: any) {
                console.error('Google sign-in backend sync failed:', err);
                setError(err.message || 'Google sign-in failed');
              }
            }}
            variant="outlined"
          />
        </Box>

        </form>
        <Typography variant="body2" align="center" sx={{ mt: 1 }}>
          Don't have an account?{' '}
          <Button onClick={() => navigate('/signup')} sx={{ color: '#FF6B35', textTransform: 'none', p: 0, minWidth: 0 }}>Sign Up</Button>
        </Typography>
        {error && <Alert severity="error" style={{ marginTop: 16 }}>{error}</Alert>}
      </Paper>
    </div>
  );
};

export default Login;